
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { RoleService } from '../../../core/services/role.service';
import { Role } from '../../../core/models/role.model';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ClientService } from '../../../core/services/client.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { UserService } from '../../../core/services/user.service';
import { NotificationService } from '../../../core/services/notification.service';

interface Application {
  id: string;
  name: string;
  icon?: string;
  description?: string;
  roles?: Role[];
}

interface RoleSelection {
  [appId: string]: {
    [roleId: string]: boolean;
  };
}

interface PendingChange {
  appId: string;
  roleId: string;
  action: 'assign' | 'unassign';
  initialState: boolean;
  currentState: boolean;
}

@Component({
  selector: 'app-user-roles-dialog',
  standalone: true,
  imports: [
    RouterModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatSelectModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatSnackBarModule,
    MatCardModule,
    MatProgressBarModule
],
  templateUrl: './user-roles-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./user-roles-dialog.component.scss']
})
export class UserRolesDialogComponent implements OnInit, OnDestroy {
  filteredApplications: Application[] = [];
  appSearchTerm: string = '';
  isLoading = false;
  userId: string = '';
  user: any = null;
  applications: Application[] = [];
  isSaving = false;
  private destroy$ = new Subject<void>();

  // Système de sélection des rôles
  roleSelection: RoleSelection = {};
  pendingChanges: PendingChange[] = [];
  private initialRoleSelection: RoleSelection = {};

  constructor(
    private roleService: RoleService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private clientService: ClientService,
    private userService: UserService,
    private notificationService: NotificationService
  ) { }

  ngOnInit(): void {
    this.userId = this.route.snapshot.paramMap.get('id') || '';

    if (!this.userId) {
      this.showErrorMessage('ID utilisateur non trouvé');
      this.router.navigate(['/dashboard/users']);
      return;
    }

    this.loadUserData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadUserData(): void {
    this.isLoading = true;

    this.userService.getUserById(this.userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (userResponse) => {
          this.user = userResponse;
          this.loadApplicationsAndRoles();
        },
        error: (error) => {
          console.error('Erreur lors du chargement utilisateur:', error);
          this.showErrorMessage('Erreur lors du chargement des données utilisateur');
          this.isLoading = false;
        }
      });
  }

  private loadApplicationsAndRoles(): void {
    this.clientService.getClientsByUser(this.userId).subscribe({
      next: (clients) => {
        this.applications = this.transformClientsToApplications(clients);

        if (this.applications.length === 0) {
          this.showErrorMessage('Aucune application trouvée pour cet utilisateur');
          this.isLoading = false;
          return;
        }

        this.loadRolesForAllApplications();
      },
      error: (error) => {
        console.error('Erreur lors du chargement des clients:', error);
        this.showErrorMessage('Erreur lors du chargement des applications');
        this.isLoading = false;
      }
    });
  }

  private loadRolesForAllApplications(): void {
    if (this.applications.length === 0) {
      this.isLoading = false;
      return;
    }

    const roleRequests = this.applications.map(app =>
      this.roleService.getClientRoles(app.id)
    );

    forkJoin(roleRequests).subscribe({
      next: (responses) => {

        // Initialiser les rôles pour chaque application
        responses.forEach((response, index) => {
          this.applications[index].roles = response.content || response || [];
        });

        // Maintenant charger les rôles utilisateur
        this.loadUserCurrentRoles();
      },
      error: (error) => {
        console.error('Erreur lors du chargement des rôles:', error);
        this.showErrorMessage('Erreur lors du chargement des rôles');
        this.isLoading = false;
      }
    });
  }

  private loadUserCurrentRoles(): void {
    const userRoleRequests = this.applications.map(app =>
      this.roleService.getUserRoles(this.userId, app.id)
    );

    forkJoin(userRoleRequests).subscribe({
      next: (responses) => {

        // Initialiser la structure de sélection
        this.initializeRoleSelection();

        // Marquer les rôles utilisateur comme sélectionnés
        this.markUserRolesAsSelected(responses);

        this.updateFilteredApplications();
        this.isLoading = false;

        // DEBUG FINAL
        this.debugFinalState();
      },
      error: (error) => {
        this.showErrorMessage('Erreur lors du chargement des rôles utilisateur');

        this.initializeRoleSelection();
        this.updateFilteredApplications();
        this.isLoading = false;
      }
    });
  }

  private initializeRoleSelection(): void {
    this.roleSelection = {};
    this.initialRoleSelection = {};

    this.applications.forEach(app => {
      if (app.roles && app.roles.length > 0) {
        this.roleSelection[app.id] = {};
        this.initialRoleSelection[app.id] = {};

        // Initialiser tous les rôles à false
        app.roles.forEach(role => {
          this.roleSelection[app.id][role.id] = false;
          this.initialRoleSelection[app.id][role.id] = false;
        });
      }
    });
  }

  private markUserRolesAsSelected(userRolesResponses: any[]): void {
    userRolesResponses.forEach((userRoles, index) => {
      const appId = this.applications[index].id;
      const appName = this.applications[index].name;

      // Extraire les rôles selon différentes structures possibles
      const rolesArray = this.extractRolesArray(userRoles);

      if (rolesArray && rolesArray.length > 0) {
        rolesArray.forEach((userRole: any) => {
          const roleId = userRole.id || userRole.roleId;
          const roleName = userRole.name || userRole.roleName;

          if (roleId) {
            if (this.roleSelection[appId] && this.roleSelection[appId][roleId] !== undefined) {
              this.roleSelection[appId][roleId] = true;
              this.initialRoleSelection[appId][roleId] = true;
            } else {
              console.warn(`❌ Rôle ${roleId} non trouvé dans l'application ${appName}`);
              console.warn(`Rôles disponibles:`, Object.keys(this.roleSelection[appId] || {}));
            }
          } else {
            // console.warn(`⚠️ ID de rôle manquant:`, userRole);
          }
        });
      } else {
        // console.log(`📭 Aucun rôle utilisateur pour ${appName}`);
      }
    });
  }

  private extractRolesArray(userRoles: any): any[] {
    if (!userRoles) return [];

    // Différentes structures possibles
    if (Array.isArray(userRoles)) {
      return userRoles;
    } else if (userRoles.content && Array.isArray(userRoles.content)) {
      return userRoles.content;
    } else if (userRoles.roles && Array.isArray(userRoles.roles)) {
      return userRoles.roles;
    } else if (userRoles.data && Array.isArray(userRoles.data)) {
      return userRoles.data;
    }

    return [];
  }

  private debugFinalState(): void {
    this.applications.forEach(app => {
      if (app.roles) {
        app.roles.forEach(role => {
          const isSelected = this.isRoleSelected(app, role);
        });
      }
    });
  }

  private updateFilteredApplications(): void {
    if (!this.appSearchTerm || this.appSearchTerm.trim() === '') {
      this.filteredApplications = [...this.applications];
    } else {
      this.filterApplications();
    }
  }

  private filterApplications(): void {
    const searchTerm = this.appSearchTerm.toLowerCase().trim();

    if (!searchTerm) {
      this.filteredApplications = [...this.applications];
    } else {
      this.filteredApplications = this.applications.filter(app =>
        app.name.toLowerCase().includes(searchTerm) ||
        app.description?.toLowerCase().includes(searchTerm)
      );
    }
  }

  onSearchChange(searchTerm: string): void {
    this.appSearchTerm = searchTerm;
    this.filterApplications();
  }

  // MÉTHODES DE SÉLECTION POUR LE TEMPLATE
  isRoleSelected(app: Application, role: Role): boolean {
    return (this.roleSelection[app.id] && this.roleSelection[app.id][role.id]);
  }

  isAppFullySelected(app: Application): boolean {
    if (!app.roles || app.roles.length === 0) return false;
    if (!this.roleSelection[app.id]) return false;

    return app.roles.every(role => this.roleSelection[app.id][role.id]);
  }

  isAppPartiallySelected(app: Application): boolean {
    if (!app.roles || app.roles.length === 0) return false;
    if (!this.roleSelection[app.id]) return false;

    const selectedRoles = app.roles.filter(role =>
      this.roleSelection[app.id][role.id]
    ).length;

    return selectedRoles > 0 && selectedRoles < app.roles.length;
  }

  toggleAppSelection(app: Application, event: any): void {
    const isSelected = event.checked;

    if (!this.roleSelection[app.id]) {
      this.roleSelection[app.id] = {};
    }

    if (app.roles) {
      app.roles.forEach(role => {
        this.roleSelection[app.id][role.id] = isSelected;
      });
    }

    this.updatePendingChanges();
  }

  toggleRoleSelection(app: Application, role: Role, event: any): void {
    if (!this.roleSelection[app.id]) {
      this.roleSelection[app.id] = {};
    }

    this.roleSelection[app.id][role.id] = event.checked;
    this.updatePendingChanges();
  }

  getSelectedRolesCount(app: Application): number {
    if (!app.roles || !this.roleSelection[app.id]) return 0;

    return app.roles.filter(role =>
      this.roleSelection[app.id][role.id]
    ).length;
  }

  getTotalSelectedRoles(): number {
    let total = 0;

    Object.keys(this.roleSelection).forEach(appId => {
      const appRoles = this.roleSelection[appId];
      Object.keys(appRoles).forEach(roleId => {
        if (appRoles[roleId]) {
          total++;
        }
      });
    });

    return total;
  }

  getTotalRoles(): number {
    return this.applications.reduce((total, app) =>
      total + (app.roles?.length || 0), 0
    );
  }

  private updatePendingChanges(): void {
    this.pendingChanges = [];

    Object.keys(this.roleSelection).forEach(appId => {
      Object.keys(this.roleSelection[appId]).forEach(roleId => {
        const currentState = this.roleSelection[appId][roleId];
        const initialState = this.initialRoleSelection[appId]?.[roleId];

        if (currentState !== initialState) {
          this.pendingChanges.push({
            appId,
            roleId,
            action: currentState ? 'assign' : 'unassign',
            initialState: !!initialState,
            currentState
          });
        }
      });
    });
  }

  get hasChanges(): boolean {
    return this.pendingChanges.length > 0;
  }

  isAppExpanded(app: Application): boolean {
    return this.getSelectedRolesCount(app) > 0 || this.filteredApplications.length <= 3;
  }

  resetAll(): void {
    Object.keys(this.initialRoleSelection).forEach(appId => {
      if (this.roleSelection[appId]) {
        Object.keys(this.initialRoleSelection[appId]).forEach(roleId => {
          this.roleSelection[appId][roleId] = this.initialRoleSelection[appId][roleId];
        });
      }
    });
    this.updatePendingChanges();
    this.showSuccessMessage('Toutes les modifications ont été réinitialisées');
  }

  selectAllAppRoles(app: Application): void {
    if (app.roles) {
      const allSelected = this.isAppFullySelected(app);

      if (!this.roleSelection[app.id]) {
        this.roleSelection[app.id] = {};
      }

      app.roles.forEach(role => {
        this.roleSelection[app.id][role.id] = !allSelected;
      });

      this.updatePendingChanges();
    }
  }

  onSave(): void {
    if (!this.hasChanges) {
      this.showSuccessMessage('Aucune modification à enregistrer');
      return;
    }

    this.isSaving = true;

    const requests = this.pendingChanges.map(change => {
      if (change.action === 'assign') {
        return this.roleService.assignClientRole(
          this.userId,
          change.roleId,
          change.appId
        );
      } else {
        return this.roleService.unassignClientRole(
          this.userId,
          change.roleId,
          change.appId
        );
      }
    });

    forkJoin(requests).subscribe({
      next: () => {
        this.isSaving = false;
        // Utiliser le nouveau service de notification
        this.notificationService.showSuccess({
          title: 'Succès',
          message: 'Rôles mis à jour avec succès',
          duration: 4000,
          showClose: true
        });

        // Mettre à jour l'état initial avec les nouvelles sélections
        this.initialRoleSelection = JSON.parse(JSON.stringify(this.roleSelection));
        this.updatePendingChanges();
      },
      error: (error) => {
        this.isSaving = false;
        this.showErrorMessage('Erreur lors de la sauvegarde : ' + (error.message || 'Erreur inconnue'));
      }
    });
  }

  onCancel(): void {
    if (this.hasChanges) {
      const confirmLeave = confirm(
        'Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter ?'
      );
      if (!confirmLeave) {
        return;
      }
    }
    this.router.navigate(['/dashboard/users']);
  }

  // MÉTHODES UTILITAIRES
  private transformClientsToApplications(clients: any[]): Application[] {
    return clients.map(client => ({
      id: client.id,
      name: client.clientId || client.name,
      description: client.description || '',
      icon: this.getAppIcon(client.clientId || client.name),
      roles: []
    }));
  }

  private getAppIcon(clientId: string): string {
    const icons: { [key: string]: string } = {
      'webapp': 'language',
      'mobile': 'smartphone',
      'admin': 'admin_panel_settings',
      'api': 'api'
    };
    return icons[clientId.toLowerCase()] || 'apps';
  }

  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 5000,
      panelClass: ['error-snackbar']
    });
  }

  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 3000,
      panelClass: ['success-snackbar']
    });
  }

  getUserInitials(firstName?: string, lastName?: string): string {
    if (!firstName && !lastName) return '?';
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  }

  getAvatarColor(username?: string): string {
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD'];
    const index = (username?.length || 0) % colors.length;
    return colors[index];
  }

  // Méthode de debug pour le template
  debugRoleSelection(): void {
    this.debugFinalState();
  }

}
